import { useMemo, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { getCurrentUser } from '../auth/useUser';
import { guideDocs, runbookDocs } from '../help/docsBundle';
import type { HelpDoc } from '../help/docs';
import { buildSearchIndex } from '../help/searchIndex';
import { renderMarkdown } from '../help/markdown';
import { ReportBugModal } from '../components/modals/ReportBugModal';

// In-portal Help page (#150) with an admin-gated runbooks section (#151). Both
// doc sets are bundled at build time (see docsBundle.ts), so the portal's help
// content tracks the repo docs on every deploy. Client-side full-text search
// spans whichever docs the current user can see — runbook hits are suppressed
// for non-admins because runbookDocs is only added to the corpus when canAdmin.

export function Help() {
  const user = getCurrentUser();
  const canAdmin = !!user?.canAdmin;

  // Corpus and known-doc-id sets are role-scoped: non-admins never receive the
  // runbook docs, so they can neither browse nor search them.
  const { allDocs, guideIds, runbookIds } = useMemo(() => {
    const guides = guideDocs;
    const runbooks = canAdmin ? runbookDocs : [];
    return {
      allDocs: [...guides, ...runbooks],
      guideIds: new Set(guides.map((d) => d.id)),
      runbookIds: new Set(runbooks.map((d) => d.id)),
    };
  }, [canAdmin]);

  const searchIndex = useMemo(() => buildSearchIndex(allDocs), [allDocs]);

  const [selectedId, setSelectedId] = useState<string>(allDocs[0]?.id ?? '');
  const [query, setQuery] = useState('');
  const [activeSearchTerms, setActiveSearchTerms] = useState<string[]>([]);
  const [reportingBug, setReportingBug] = useState(false);

  const selected = allDocs.find((d) => d.id === selectedId) ?? allDocs[0];

  const results = useMemo(() => (query.trim() ? searchIndex.search(query) : []), [query, searchIndex]);

  const openDoc = (id: string, terms: string[] = []) => {
    setSelectedId(id);
    setActiveSearchTerms(terms);
    // Scroll the content pane back to the top on section change.
    const pane = document.getElementById('help-content-pane');
    if (pane) pane.scrollTop = 0;
  };

  // Same-group id set for link classification in the rendered doc.
  const knownDocIds = selected?.group === 'runbook' ? runbookIds : guideIds;

  const sectionButton = (d: HelpDoc) => (
    <button
      key={d.id}
      type="button"
      onClick={() => openDoc(d.id)}
      aria-current={selected?.id === d.id ? 'true' : undefined}
      style={{
        ...styles.btnUnstyled,
        display: 'block',
        width: '100%',
        padding: '7px 12px',
        borderRadius: 6,
        fontSize: 13,
        color: selected?.id === d.id ? C.text : C.textSecondary,
        background: selected?.id === d.id ? C.goldBg : 'transparent',
        fontWeight: selected?.id === d.id ? 600 : 400,
      }}
    >
      {d.title}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Help &amp; Documentation</h2>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>
            The BCID Portal user guide{canAdmin ? ' and administrator runbooks' : ''}, bundled with this release.
          </div>
        </div>
        <button
          type="button"
          style={{ ...styles.btn, ...styles.btnOutline, whiteSpace: 'nowrap', flexShrink: 0 }}
          onClick={() => setReportingBug(true)}
        >
          Report a bug
        </button>
      </div>

      {reportingBug && <ReportBugModal page="help" onClose={() => setReportingBug(false)} />}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, alignItems: 'start' }}>
        {/* Left rail: search + section index */}
        <div style={{ ...styles.card, padding: 16, position: 'sticky', top: 0 }}>
          <label htmlFor="help-search" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 4 }}>
            Search the documentation
          </label>
          <input
            id="help-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            style={{ ...styles.input, padding: '8px 12px' }}
          />

          {query.trim() ? (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>
                {results.length} result{results.length === 1 ? '' : 's'}
              </div>
              {results.map((hit) => (
                <button
                  key={hit.doc.id}
                  type="button"
                  onClick={() => openDoc(hit.doc.id, hit.matchedTerms)}
                  style={{ ...styles.btnUnstyled, display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, marginBottom: 6, background: C.bgInput }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>
                    {hit.doc.title}
                    {hit.doc.group === 'runbook' && (
                      <span style={{ ...styles.badge, marginLeft: 6, background: C.infoBg, color: C.info, fontSize: 9 }}>RUNBOOK</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.4, marginTop: 2 }}>{hit.snippet}</div>
                </button>
              ))}
              {results.length === 0 && (
                <div style={{ fontSize: 12, color: C.textMuted, padding: '8px 0' }}>No matching sections.</div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', margin: '4px 0 6px' }}>
                User guide
              </div>
              {guideDocs.map(sectionButton)}

              {canAdmin && runbookDocs.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', margin: '16px 0 6px' }}>
                    Admin runbooks
                  </div>
                  {runbookDocs.map(sectionButton)}
                </>
              )}
            </div>
          )}
        </div>

        {/* Right pane: rendered doc */}
        <div id="help-content-pane" style={{ ...styles.card, maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
          {selected ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>{selected.title}</h1>
                {selected.group === 'runbook' && (
                  <span style={{ ...styles.badge, background: C.infoBg, color: C.info }}>ADMIN RUNBOOK</span>
                )}
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, marginBottom: 16, paddingTop: 4 }} />
              {renderMarkdown({
                doc: selected,
                knownDocIds,
                onNavigate: (id) => openDoc(id),
                highlight: activeSearchTerms,
              })}
            </>
          ) : (
            <div style={{ fontSize: 13, color: C.textMuted }}>No documentation is bundled with this build.</div>
          )}
        </div>
      </div>
    </div>
  );
}
